import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Target,
  Layers,
  Globe,
  Webhook,
  Key,
  Users,
  Link2,
  BarChart3,
  Settings,
  UserSquare2,
  Plug,
  CreditCard,
  LogOut,
  ChevronLeft,
  Lock,
  X,
} from "lucide-react";
import { Wordmark, BrandMark } from "./Brand";
import { useAuth } from "../../context/AuthContext";

const STORAGE_KEY = "linklane_sidebar_collapsed";

// Plan gate helpers — rank-based so a higher tier satisfies a lower gate.
const PLAN_RANK = { free: 0, pro: 1, growth: 2, enterprise: 3 };
const canAccess = (gate, user) => {
  if (!gate) return true;
  if (!user) return false;
  if (user.role === "admin") return true;
  return (PLAN_RANK[user.plan] ?? 0) >= (PLAN_RANK[gate] ?? 0);
};

const NAV_GROUPS = [
  {
    group: null,
    items: [{ label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" }],
  },
  {
    group: "Link Management",
    items: [
      { label: "Links", icon: Link2, to: "/links" },
      { label: "Campaigns", icon: Target, to: "/campaigns", gate: "pro" },
      { label: "Bulk Operations", icon: Layers, to: "/bulk", gate: "pro" },
    ],
  },
  {
    group: "Creator Tools",
    items: [{ label: "Link in Bio", icon: UserSquare2, to: "/bio/edit", gate: "pro" }],
  },
  {
    group: "Analytics",
    items: [{ label: "Analytics", icon: BarChart3, to: "/analytics", gate: "pro" }],
  },
  {
    group: "Automation",
    items: [
      { label: "API Keys", icon: Key, to: "/api-keys", gate: "pro" },
      { label: "Webhooks", icon: Webhook, to: "/webhooks", gate: "pro" },
      { label: "Integrations", icon: Plug, to: "/integrations", gate: "pro" },
    ],
  },
  {
    group: "Workspace",
    items: [
      { label: "Team", icon: Users, to: "/team", gate: "enterprise" },
      { label: "Domains", icon: Globe, to: "/domains", gate: "pro" },
      { label: "Billing", icon: CreditCard, to: "/billing" },
      { label: "Settings", icon: Settings, to: "/settings" },
    ],
  },
];

// ─── Single nav item ─────────────────────────────────────────────────────────
function NavItem({ item, collapsed, user }) {
  const accessible = canAccess(item.gate, user);
  const Icon = item.icon;

  return (
    <li>
      <NavLink
        // Always route to the page itself — gated pages render an in-app upsell
        // (PlanGate full-page) explaining the feature + trial/upgrade CTA, rather
        // than dumping free users straight onto /pricing.
        to={item.to}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          [
            "relative flex items-center gap-3 rounded-xl text-sm font-semibold",
            "select-none transition-colors duration-150",
            collapsed ? "mx-auto w-10 justify-center px-0 py-2.5" : "px-3 py-2.5",
            isActive
              ? "bg-candy/10 text-candy"
              : !accessible
                ? "text-ink/55 hover:bg-ink/5 hover:text-ink/80"
                : "text-ink/65 hover:bg-ink/5 hover:text-ink",
          ].join(" ")
        }
      >
        <Icon size={17} strokeWidth={2} className="shrink-0" />
        {!collapsed && <span className="flex-1 whitespace-nowrap leading-none">{item.label}</span>}
        {!collapsed && !accessible && (
          <Lock size={10} strokeWidth={2.5} className="shrink-0 text-ink/60" />
        )}
      </NavLink>
    </li>
  );
}

// ─── Sidebar core (reused for desktop + mobile) ───────────────────────────────
function SidebarCore({ collapsed, onToggleCollapse, showCollapseBtn, user, onLogout }) {
  const coreNavigate = useNavigate();
  const planBadge = user
    ? user.role === "admin"
      ? { label: "ADMIN", cls: "bg-candy/15 text-candy border-candy/30" }
      : user.plan === "enterprise"
        ? { label: "ENT", cls: "bg-mint/20 text-ink border-mint/30" }
        : user.plan === "growth"
          ? { label: "GROWTH", cls: "bg-pink/20 text-ink border-pink/40" }
          : user.plan === "pro"
            ? { label: "PRO", cls: "bg-yellow/30 text-ink border-yellow/40" }
            : { label: "FREE", cls: "bg-ink/8 text-ink/50 border-ink/15" }
    : null;

  const userInitial =
    user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "?";

  return (
    <div className="flex h-full flex-col overflow-hidden border-r-2 border-ink bg-white">
      {/* ── Logo header ─────────────────────────────────────── */}
      <div
        className={[
          "flex h-[65px] shrink-0 items-center border-b-2 border-ink px-4",
          collapsed ? "justify-center" : "justify-between",
        ].join(" ")}
      >
        {collapsed ? (
          <button
            onClick={onToggleCollapse}
            className="rounded-lg p-1 transition-colors hover:bg-ink/5"
            aria-label="Expand sidebar"
          >
            <BrandMark size="sm" tone="ink" animate={false} />
          </button>
        ) : (
          <>
            <Wordmark size="sm" tone="ink" accent="candy" />
            {showCollapseBtn && (
              <button
                onClick={onToggleCollapse}
                className="shrink-0 rounded-lg p-1.5 text-ink/60 transition-colors hover:bg-ink/5 hover:text-ink"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft size={15} strokeWidth={2.5} />
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Nav groups ──────────────────────────────────────── */}
      <nav className="flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-2 py-3">
        {NAV_GROUPS.map((group, gi) => {
          // Show all items — gated ones render locked (discoverable, route to /pricing)
          const visibleItems = group.items;
          if (visibleItems.length === 0) return null;

          return (
            <div key={gi}>
              {group.group && !collapsed && (
                <p className="mb-1 select-none px-3 font-mono text-2xs uppercase tracking-[0.22em] text-ink/60">
                  {group.group}
                </p>
              )}
              {group.group && collapsed && <div className="mx-auto mb-1 h-px w-5 bg-ink/10" />}
              <ul className="space-y-0.5">
                {visibleItems.map((item) => (
                  <NavItem key={item.to} item={item} collapsed={collapsed} user={user} />
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* ── User footer ─────────────────────────────────────── */}
      <div className="shrink-0 space-y-0.5 border-t-2 border-ink px-2 py-3">
        {/* Avatar + info row */}
        <div
          className={[
            "flex items-center gap-3 rounded-xl px-2 py-2",
            collapsed ? "justify-center" : "",
          ].join(" ")}
          title={collapsed ? `${user?.email}` : undefined}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border-2 border-ink bg-candy font-display text-sm font-bold text-white shadow-sticker-xs">
            {userInitial}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-2xs leading-tight tracking-[0.14em] text-ink/60">
                {user?.email}
              </p>
              {planBadge &&
                (planBadge.label === "FREE" ? (
                  <button
                    type="button"
                    onClick={() => coreNavigate("/billing")}
                    className={`mt-1 inline-block cursor-pointer rounded-full border px-1.5 py-0.5 font-mono text-2xs font-bold uppercase tracking-[0.22em] transition-opacity hover:opacity-75 ${planBadge.cls}`}
                    title="Upgrade your plan"
                  >
                    {planBadge.label}
                  </button>
                ) : (
                  <span
                    className={`mt-1 inline-block rounded-full border px-1.5 py-0.5 font-mono text-2xs font-bold uppercase tracking-[0.22em] ${planBadge.cls}`}
                  >
                    {planBadge.label}
                  </span>
                ))}
            </div>
          )}
        </div>

        {/* Sign out */}
        <button
          onClick={onLogout}
          title={collapsed ? "Sign out" : undefined}
          data-testid="sidebar-logout"
          className={[
            "flex w-full items-center gap-3 rounded-xl px-3 py-2",
            "hover:bg-pink/8 text-sm font-semibold text-ink/60 hover:text-pink",
            "transition-colors duration-150",
            collapsed ? "justify-center" : "",
          ].join(" ")}
        >
          <LogOut size={15} strokeWidth={2} className="shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function AppSidebar({ mobileOpen, onMobileClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Persist collapse state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  // Close mobile drawer on navigation
  useEffect(() => {
    onMobileClose?.();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const toggleCollapse = () => setCollapsed((v) => !v);

  return (
    <>
      {/* ── Desktop sidebar ───────────────────────────────── */}
      <aside
        data-testid="app-sidebar"
        className={[
          "hidden h-full shrink-0 flex-col md:flex",
          "transition-all duration-200 ease-out",
          collapsed ? "w-[68px]" : "w-[240px]",
        ].join(" ")}
        style={{ minWidth: collapsed ? 68 : 240 }}
      >
        <SidebarCore
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          showCollapseBtn={true}
          user={user}
          onLogout={onLogout}
        />
      </aside>

      {/* ── Mobile overlay drawer ─────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] flex md:hidden" aria-modal="true">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-[3px]" onClick={onMobileClose} />
          {/* Drawer panel */}
          <div
            className="relative h-full w-[240px] shrink-0"
            style={{ animation: "sidebarSlideIn 200ms ease-out both" }}
          >
            <style>{`
                            @keyframes sidebarSlideIn {
                                from { transform: translateX(-100%); }
                                to   { transform: translateX(0); }
                            }
                        `}</style>
            <SidebarCore
              collapsed={false}
              onToggleCollapse={() => {}}
              showCollapseBtn={false}
              user={user}
              onLogout={onLogout}
            />
          </div>
          {/* Close button */}
          <button
            onClick={onMobileClose}
            className="absolute left-[256px] top-4 z-50 rounded-xl border-2 border-ink bg-white p-1.5 shadow-sticker-xs"
            aria-label="Close menu"
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </>
  );
}
